import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import React, { useEffect } from "react";
import { Folder } from "../../redux/interfaces/folder.types.ts";
import { Form, FormProps, message, Popconfirm, Select } from "antd";
import {
  fetchFolderHistory,
  onReassignCollaborator,
  onReassignUser,
} from "../../redux/actions/folderActions.tsx";
import { useParams } from "react-router-dom";
import { User } from "../../redux/interfaces/user.types.ts";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";

interface Props {
  folder: Folder;
  type: "user" | "collaborator";
}

interface Assigned {
  assigned?: string | null;
}

export const Assigned: React.FC<Props> = ({ folder, type }) => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [newAssigned, setNewAssigned] = React.useState<string | null>("");
  const [originalAssigned, setOriginalAssigned] = React.useState<string | null>(
    "",
  );

  const { userInfo } = useAppSelector((state) => state.auth);
  const { results: users } = useAppSelector((state) => state.user);
  const { activeFolder } = useAppSelector((state) => state.folder);
  const isAdmin = userInfo?.groups.includes("admin");

  const titleAssign =
    type === "user"
      ? `¿Asignar expediente a ${newAssigned}?`
      : `¿Asignar colaborador a ${newAssigned}?`;

  const open = !!newAssigned && newAssigned !== originalAssigned;

  const filter =
    type === "user"
      ? (user: User) => user.groups.includes("operations")
      : (user: User) => user.groups.includes("cja");

  const AssignedComponent: React.FC<Assigned> = ({ assigned }) => (
    <div className="flex items-center">
      <span>
        {type === "user"
          ? `Asignado: ${assigned || ""}`
          : `Colaborador: ${assigned || ""}`}
      </span>
    </div>
  );

  const onSetAssigned = () => {
    const assigned =
      type === "user" ? folder.assigned_user : folder.collaborator;

    form.setFieldValue("new_assigned", assigned);
    setNewAssigned(assigned);
    setOriginalAssigned(assigned);
  };

  const onToggleAssigned = (assigned: string) => {
    form.setFieldValue("new_assigned", assigned);
    setNewAssigned(assigned);
  };

  const onResetAssigned = () => {
    form.setFieldValue("new_assigned", originalAssigned);
    setNewAssigned(originalAssigned);
  };

  const onReassign = async (assigned: User) => {
    try {
      if (type === "user") {
        await dispatch(
          onReassignUser({
            folder_id: folder.id,
            user_id: assigned!.id,
          }),
        ).unwrap();
      } else {
        await dispatch(
          onReassignCollaborator({
            folder_id: folder.id,
            user_id: assigned!.id,
          }),
        ).unwrap();
      }
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudo reasignar el expediente."));
    }
  };

  const onChangeAssigned: FormProps["onFinish"] = async ({
    new_assigned: email,
  }) => {
    const assigned = users.find((user) => user.email === email);

    const successAssignedUserMsg = `Expediente reasignado a ${assigned?.name} - ${assigned?.email}`;
    const successAssignedCollabMsg = `Colaborador reasignado: ${assigned?.name} - ${assigned?.email}`;

    try {
      await onReassign(assigned!);

      if (id) await dispatch(fetchFolderHistory(id)).unwrap();

      const msg =
        type === "user" ? successAssignedUserMsg : successAssignedCollabMsg;
      messageApi.success(msg);

      setOriginalAssigned(email);
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudo asignar el expediente."));
    }
  };

  useEffect(() => {
    onSetAssigned();
  }, [folder.assigned_user, folder.collaborator, activeFolder]);

  if (!isAdmin) {
    return <AssignedComponent assigned={originalAssigned || "N/A"} />;
  }

  return (
    <div className="w-full flex items-center justify-start">
      {contextHolder}
      <AssignedComponent />
      &nbsp;
      <Form form={form} onFinish={onChangeAssigned}>
        <Form.Item
          name="new_assigned"
          className="w-96"
          style={{ marginBottom: 0 }}
        >
          <Select className="w-96" onChange={onToggleAssigned}>
            {users
              .filter((user) => filter(user))
              .map((user) => (
                <Select.Option key={user.email} value={user.email}>
                  {user.name} - {user.email}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Form>
      <Popconfirm
        title={titleAssign}
        open={open}
        onConfirm={() => {
          setNewAssigned("");
          form.submit();
        }}
        onCancel={onResetAssigned}
        okText="Sí"
        cancelText="No"
      />
    </div>
  );
};
